import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation } from "@tanstack/react-query";
import { getFileTitle } from "../components/utils";

interface UseDownloadSecurityClearanceAttachmentsOptions {
  notify?: (args: { title: string; description?: string; type: "success" | "error" }) => void;
  t?: (key: string) => string;
}

export const useDownloadSecurityClearanceAttachments = ({
  notify,
  t,
}: UseDownloadSecurityClearanceAttachmentsOptions = {}) => {
  const attachmentMutation = useMutation({
    mutationFn: async ({ url }: { url: string }) => {
      return getDocumentsWithToken(url, SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE);
    },
  });

  const getExt = (u: string) =>
    (u.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();

  const isPdf = (ext: string, mime: string) => ext === "pdf" || mime === "application/pdf";

  const isImage = (ext: string, mime: string) =>
    mime.startsWith("image/") || ext === "jpg" || ext === "jpeg" || ext === "png";

  const toViewableMime = (ext: string, mime: string) => {
    if (isPdf(ext, mime)) return "application/pdf";
    if (mime.startsWith("image/")) return mime;
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    if (ext === "png") return "image/png";
    return "application/octet-stream";
  };

  const downloadAttachment = async (url: string, filename?: string) => {
    try {
      const blob = await attachmentMutation.mutateAsync({ url });
      const finalName =
        filename || decodeURIComponent(getFileTitle(url, undefined) || "attachment");
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
    } catch (err: any) {
      notify?.({
        title: t ? t("common.error") : "Error",
        description: err?.message || (t ? t("common.error") : "Error"),
        type: "error",
      });
      throw err;
    }
  };

  const handlePreview = async (documentUrl: string) => {
    try {
      const blob = await attachmentMutation.mutateAsync({ url: documentUrl });
      const ext = getExt(documentUrl);
      const mime = blob.type || "";

      if (isPdf(ext, mime) || isImage(ext, mime)) {
        const viewMime = toViewableMime(ext, mime);
        const objUrl = URL.createObjectURL(new Blob([blob], { type: viewMime }));
        window.open(objUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      notify?.({
        title: t ? t("common.error") : "Error",
        description: err?.message || (t ? t("common.error") : "Error"),
        type: "error",
      });
      throw err;
    }
  };

  return {
    downloadAttachment,
    handlePreview,
    isPending: attachmentMutation.isPending,
    isError: attachmentMutation.isError,
    error: attachmentMutation.error,
  };
};
